import { Tag, TagLabel } from '@chakra-ui/react';
import React from 'react';
import { z } from 'zod';

import {
  date as commonDate,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { User, UserType } from '~/contexts/UserAuthProvider';
import { Icon } from '~/packages/common-frontend/components/icons';

// Types
export type FacilitiesTableRowData = {
  creditFacilityName: string;
  externalFacilityId: string | null;
  globalCommitmentAmount: number;
  globalLoanOutstanding: number;
  globalUnusedCommitment: number;
  globalUtilization: number;

  capitalProviderCommitmentAmount: number;
  capitalProviderLoanOutstanding: number;
  capitalProviderUnused: number;
  capitalProviderUtilization: number;

  dateOfLastFundingRequest?: Util.FullDateStr;
  collectionsAccountBalance: number;
  borrowingBaseFromReceivables: number;
  borrowingBaseCashContribution: number;
  requiredReserve: number;
  totalBorrowingBase: number;
  interestRates: string;
  accruedInterestAndFees: number;
  financialCovenantsFailed: number | null; // 0 failed means pass, null means no calculations exist
  portfolioCovenantsFailed: number | null; // 0 failed means pass, null means no calculations exist
};
export type FacilityTableColumnKey = keyof FacilitiesTableRowData;
type FacilityTableRowConfig = {
  header: string;
  fixed?: boolean;
  userTypes: UserType[];
  render: (cellValue: unknown) => React.ReactNode;
  relatedToBorrowingBase: boolean;
};

// Todo: Valley sls Dealing with optional externalFacilityId as a column
type FacilitiesTableConfig = Omit<
  Record<FacilityTableColumnKey, FacilityTableRowConfig>,
  'externalFacilityId'
> & {
  externalFacilityId?: FacilityTableRowConfig;
};

// Helpers
export const renderDollarCell = (cellValue: unknown) => {
  const number = z.number().parse(cellValue);
  const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
  return formatted ?? number;
};
const renderStringCell = (cellValue: unknown) => z.string().parse(cellValue);
const renderMaybeStringCell = (cellValue: unknown) =>
  cellValue ? z.string().parse(cellValue) : '-';
const renderDateCell = (cellValue: unknown) =>
  commonTypeguards.isFullDateStr(cellValue)
    ? commonDate.fullDateStrToHumanDateString(cellValue)
    : '-';
const renderPercentCell = (cellValue: unknown) => {
  const number = z.number().parse(cellValue);
  const formatted = commonUtils.numberToFormattedString(
    number,
    'percentage',
    false,
  );
  return formatted ?? number;
};
const renderStatusCell = (cellValue: unknown) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (typeof cellValue !== 'number') return '-';
  const isError = cellValue > 0;
  return (
    <Tag
      variant="subtle"
      size="lg"
      colorScheme={isError ? 'red' : 'green'}
      gap="0.5rem"
    >
      {isError ? (
        <>
          <Icon icon="circle-x" />
          <TagLabel>{cellValue} Failed</TagLabel>
        </>
      ) : (
        <>
          <Icon icon="circle-check" />
          <TagLabel>Passed</TagLabel>
        </>
      )}
    </Tag>
  );
};

// Exports
export const FACILITIES_TABLE_CONFIG: (user: User) => FacilitiesTableConfig = (
  user,
) => {
  const isValleySls = user.organization.slug === 'valleyBankSls';

  return {
    creditFacilityName: {
      header: 'Facility',
      fixed: true,
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStringCell,
      relatedToBorrowingBase: false,
    },

    ...(isValleySls && {
      externalFacilityId: {
        header: 'External Facility ID',
        userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
        render: renderMaybeStringCell,
        relatedToBorrowingBase: false,
      },
    }),

    globalCommitmentAmount: {
      header: 'Global Commitment Amount',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: false,
    },

    globalLoanOutstanding: {
      header: 'Global Outstanding Balance',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: false,
    },

    globalUnusedCommitment: {
      header: 'Global Unused Commitment',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    globalUtilization: {
      header: 'Global Utilization',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderPercentCell,
      relatedToBorrowingBase: true,
    },

    capitalProviderCommitmentAmount: {
      header: '{organizationName} Commitment Amount',
      userTypes: ['CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    capitalProviderLoanOutstanding: {
      header: '{organizationName} Outstanding Balance',
      userTypes: ['CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    capitalProviderUnused: {
      header: '{organizationName} Unused Commitment',
      userTypes: ['CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    capitalProviderUtilization: {
      header: '{organizationName} Utilization',
      userTypes: ['CAPITAL_PROVIDER'],
      render: renderPercentCell,
      relatedToBorrowingBase: true,
    },

    collectionsAccountBalance: {
      header: 'Collections Account Balance',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    requiredReserve: {
      header: 'Required Reserve',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    totalBorrowingBase: {
      header: 'Total Borrowing Base',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    borrowingBaseFromReceivables: {
      header: 'Borrowing Base from Receivables',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    borrowingBaseCashContribution: {
      header: 'Borrowing Base from Cash',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: true,
    },

    accruedInterestAndFees: {
      header: 'Accrued Interest and Fees',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDollarCell,
      relatedToBorrowingBase: false,
    },

    interestRates: {
      header: 'Interest Rate',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStringCell,
      relatedToBorrowingBase: false,
    },

    dateOfLastFundingRequest: {
      header: 'Date of Last Funding Request',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderDateCell,
      relatedToBorrowingBase: true,
    },

    financialCovenantsFailed: {
      header: 'Financial Covenants',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStatusCell,
      relatedToBorrowingBase: true,
    },

    portfolioCovenantsFailed: {
      header: 'Portfolio Covenants',
      userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
      render: renderStatusCell,
      relatedToBorrowingBase: true,
    },
  };
};
